import { z } from 'zod';

export const LevelTileSchema = z.object({
  item: z.string().min(1),
  layer: z.number().int().min(0).max(3),
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  wrapped: z.boolean().optional(),
  ice: z.number().int().min(1).max(2).optional(),
  /** glue pair id (rules v2): exactly two adjacent tiles on one layer share it (core validateLevel checks the pairing) */
  glue: z.number().int().min(1).optional(),
  /** padlocked until the level's key item pops (rules v2, lockKey; core validateLevel checks the rest) */
  locked: z.literal(true).optional(),
  /** golden board marker (rules v2, collectGoal; core validateLevel checks goal, count and exclusions) */
  golden: z.literal(true).optional(),
});

export const MechanicSchema = z.enum(['clutter', 'wrapped', 'twoCustomers', 'rush', 'crates', 'ice', 'conveyor', 'glue', 'bagLock', 'dispenser', 'lockKey', 'clearGoal', 'hiddenLine', 'collectGoal', 'tray', 'threeCustomers', 'shelf']);

export const LevelSchema = z.object({
  v: z.literal(1),
  id: z.string().regex(/^[a-z]+-\d{3,}$|^daily-\d{4}-\d{2}-\d{2}$|^[a-z0-9-]+$/),
  city: z.string(),
  shop: z.string(),
  number: z.number().int().min(0),
  rulesVersion: z.number().int().min(1),
  grid: z.object({ w: z.number().int(), h: z.number().int() }),
  tiles: z.array(LevelTileSchema).min(1),
  crates: z.array(z.object({ x: z.number().int(), y: z.number().int(), layer: z.number().int().min(0).max(3), hp: z.number().int().min(1).max(3) })).optional(),
  conveyor: z.object({ slots: z.number().int().min(1).max(4), items: z.array(z.string()) }).optional(),
  /** dispensers (rules v2): machines holding a stack of items, top first (core validateLevel checks overlaps and rulesVersion) */
  dispensers: z
    .array(z.object({ x: z.number().int().min(0), y: z.number().int().min(0), layer: z.number().int().min(0).max(3), items: z.array(z.string().min(1)).min(3).max(8) }))
    .min(1)
    .max(2)
    .optional(),
  /** trays (rules v2): pre-made triples of one item at one board cell (core validateLevel checks the grid, overlaps and rulesVersion) */
  trays: z
    .array(z.object({ x: z.number().int().min(0), y: z.number().int().min(0), layer: z.number().int().min(0).max(3), item: z.string().min(1) }))
    .min(1)
    .max(3)
    .optional(),
  /** shelves (rules v2): one side shelf of 4..8 items, left to right, only its two ends tappable (core validateLevel checks rulesVersion) */
  shelves: z
    .array(z.object({ items: z.array(z.string().min(1)).min(4).max(8) }))
    .min(1)
    .max(1)
    .optional(),
  /** one ticket card per customer: up to 2 on rules v1, 3 on rules v2 (threeCustomers; core validateLevel checks rulesVersion) */
  customers: z
    .array(
      z.object({
        id: z.string(),
        /** `hidden` (rules v2, hiddenLine): the line is face down until revealed (core validateLevel checks placement, count and rulesVersion) */
        lines: z.array(z.object({ item: z.string(), count: z.number().int(), hidden: z.literal(true).optional() })).min(1).max(4),
      }),
    )
    .min(1)
    .max(3),
  bag: z.number().int(),
  /** locked bag slots (rules v2): open when the first ticket line completes (core validateLevel checks rulesVersion and room) */
  bagLock: z.number().int().min(1).max(2).optional(),
  /** lock and key (rules v2): the item whose first triple opens every `locked` tile (core validateLevel checks count, key and rulesVersion) */
  lockKey: z.object({ key: z.string().min(1) }).optional(),
  /** round goal (rules v2): absent = order; clear = whole board; collect = ticket plus golden tiles */
  goal: z.enum(['clear', 'collect']).optional(),
  rush: z.object({ ms: z.number().int() }).optional(),
  mechanics: z.array(MechanicSchema),
  par: z.number().int().min(1),
  reward: z.object({ coins: z.number().int().min(0) }),
  assist: z.array(z.tuple([z.string(), z.string()])).max(2).optional(),
  /** lookalike clutter item -> substitute item for the accessibility setting (presentation only, not hashed; core validateLevel checks keys and values) */
  lookalikes: z.record(z.string(), z.string()).optional(),
  stats: z.object({ greedyWinRate: z.number().int(), intendedTaps: z.number().int(), tiles: z.number().int(), layers: z.number().int() }).optional(),
  generator: z.object({ version: z.number().int(), seed: z.number().int(), band: z.tuple([z.number(), z.number()]), template: z.string(), parWidth: z.number().int().optional() }).optional(),
  hash: z.string().optional(),
});
export type LevelJson = z.infer<typeof LevelSchema>;
